import React, { useEffect, useState } from "react";

import { Card, CardTitle, Icon } from "./ui";
import useWorkSummary from "../../../hooks/useWorkSummary";

/*
  미조치 위반이 하나도 없을 때 «안전 위반 알림» 자리에 대신 서는 화면.

  왜 필요한가
    위반이 없으면 화면에서 제일 큰 자리가 "없습니다" 두 줄만 띄우고 논다.
    그렇다고 평소에 차트를 깔아두면 정작 위반이 떴을 때 그게 묻힌다.
    그래서 «한 자리에 두 얼굴»로 간다 — 평시엔 현황판, 위반이 생기면 즉시 위반 화면.
    둘이 동시에 보이지 않으므로 난잡해지지 않는다.

  가운데 큰 숫자는 «무위반 경과 시간»이다. 현장 안전게시판의 "무재해 OO일"과 같은 자리다.
  관리자가 대문에서 가장 먼저 확인하고 싶은 한 가지가 그거다.

  숫자는 전부 /api/safety-report 실데이터다.
*/

function minutesSince(iso) {
  const t = Date.parse(String(iso || "").replace(" ", "T"));
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

function fmtClock(iso) {
  const m = String(iso || "").match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "-";
}

function Trend({ daily = [] }) {
  const rows = (Array.isArray(daily) ? daily : []).slice(-7);
  const counts = rows.map((d) => Number(d?.count || 0));
  if (!counts.length) return null;

  const max = Math.max(1, ...counts);
  const total = counts.reduce((a, b) => a + b, 0);
  const avg = Math.round((total / counts.length) * 10) / 10;

  /*
    높이는 남는 세로를 다 쓴다(차트가 작으면 추세가 안 읽힌다).
    폭만 고정(22px)해서 가늘게 — 칸 너비를 꽉 채웠더니 큰 덩어리 7개가 돼 조악했다.
    % 높이가 먹으려면 줄과 칸에 높이가 내려가 있어야 한다(h-full).
  */

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 items-baseline gap-2">
        <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
          최근 7일 위반 추이
        </span>
        <span className="ml-auto text-[11.5px] tabular-nums text-slate-400">
          하루 평균 {avg}건 · 합계 {total}건
        </span>
      </div>

      <div className="relative min-h-0 flex-1">
        {/* 바닥선 — 0 인 날도 기준선 위에 놓여 «데이터 없음»과 구분된다. */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-slate-200 dark:bg-white/10" />

        <div className="flex h-full items-end">
          {counts.map((c, i) => {
            const isToday = i === counts.length - 1;
            return (
              <div
                key={rows[i]?.date || i}
                className="flex h-full min-w-0 flex-1 flex-col items-center justify-end"
              >
                {c > 0 && (
                  <span
                    className={`mb-1 shrink-0 text-[11px] font-semibold tabular-nums ${
                      isToday ? "text-blue-600 dark:text-blue-400" : "text-slate-400"
                    }`}
                  >
                    {c}
                  </span>
                )}
                <div
                  className={`w-[22px] shrink-0 rounded-t-[4px] ${
                    isToday
                      ? "bg-blue-500"
                      : c === 0
                      ? "bg-slate-200 dark:bg-white/10"
                      : "bg-slate-300 dark:bg-white/25"
                  }`}
                  style={{ height: c === 0 ? 2 : `${Math.max(4, (c / max) * 88)}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex shrink-0 text-[10.5px] tabular-nums text-slate-400">
        {rows.map((d, i) => (
          <span
            key={d.date}
            className={`min-w-0 flex-1 text-center ${
              i === rows.length - 1 ? "font-semibold text-slate-500 dark:text-slate-300" : ""
            }`}
          >
            {i === rows.length - 1 ? "오늘" : String(d.date).slice(5).replace("-", "/")}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function CalmBoard({ today, week, onNavigate }) {
  const [, setTick] = useState(0);
  const { data: work } = useWorkSummary();

  // 경과 시간이 멈춰 있으면 화면이 죽은 것처럼 보인다.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const all = Array.isArray(today?.violations) ? today.violations : [];
  const doneList = all
    .filter((v) => v?.status !== "open")
    .sort((a, b) => String(b?.timestamp || "").localeCompare(String(a?.timestamp || "")));

  const todayCount = Number(today?.summary?.violationCount || 0);
  const compliance = week?.summary?.complianceRate;

  // 마지막 위반 이후 경과. 오늘 한 건도 없으면 «오늘 위반 없음».
  const lastAt = all.length
    ? all
        .map((v) => v?.timestamp)
        .sort((a, b) => String(b || "").localeCompare(String(a || "")))[0]
    : null;
  const sinceMin = lastAt ? minutesSince(lastAt) : null;

  const bigValue =
    sinceMin == null
      ? "0"
      : sinceMin < 60
      ? String(sinceMin)
      : String(Math.floor(sinceMin / 60));
  const bigUnit =
    sinceMin == null ? "건" : sinceMin < 60 ? "분째" : "시간째";
  const bigNote =
    sinceMin == null
      ? "오늘 감지된 위반이 없습니다"
      : `마지막 위반 ${fmtClock(lastAt)} 이후 무위반`;

  const wc = work?.counts;
  const workTotal = Number(wc?.total || 0);
  const workDone = Number(wc?.done || 0);
  const workWorking = Number(wc?.working || 0);
  const workPct = workTotal > 0 ? Math.round((workDone / workTotal) * 100) : 0;

  const jc = work?.jobCompliance;
  const jobTotal = Number(jc?.total || 0);
  const jobViol = Number(jc?.violationJobs || 0);
  const jobRate = jc?.rate == null ? null : Number(jc.rate);

  const byItem = (Array.isArray(today?.byItem) ? today.byItem : []).slice(0, 3);
  const maxItem = Math.max(1, ...byItem.map((r) => Number(r?.count || 0)));

  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <CardTitle
        title="안전 현황"
        right={
          <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="h-[6px] w-[6px] rounded-full bg-emerald-500 ring-[3px] ring-emerald-100 dark:ring-emerald-500/20" />
            미조치 없음
          </span>
        }
      />

      <div className="flex min-h-0 flex-1">
        {/* 왼쪽 — 무위반 경과 + 오늘 숫자 + 추이 */}
        <div className="flex min-w-0 flex-1 flex-col p-6">
          <div className="flex shrink-0 gap-6">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
                무위반 경과
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-[56px] font-bold leading-none tracking-[-0.03em] text-slate-900 tabular-nums dark:text-white">
                  {bigValue}
                </span>
                <span className="text-[20px] font-semibold text-slate-400">{bigUnit}</span>
              </div>
              <div className="mt-2 text-[12.5px] text-slate-500 dark:text-slate-400">
                {bigNote}
              </div>
            </div>

            {/*
              오늘 작업 진행 — «몇 % 했고, 그중 몇 건에서 위반이 났나».
              진행률만 보면 안전과 연결이 안 되고, 위반 건수만 보면 모수를 모른다.
              작업 단위 준수율(jobCompliance)은 서버가 «작업 N건 중 M건에서 위반»으로 집계한다.
            */}
            {work && (
              <div className="w-[300px] shrink-0 rounded-xl border border-slate-200 p-4 dark:border-white/10">
                <div className="flex items-baseline">
                  <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">
                    오늘 작업 진행
                  </span>
                  <span className="ml-auto text-[12.5px] tabular-nums text-slate-400">
                    {workTotal > 0 ? `${workDone} / ${workTotal}건` : "0건"}
                  </span>
                </div>

                {workTotal === 0 ? (
                  <div className="mt-3 text-[12px] text-slate-400">
                    오늘 예정된 작업이 없습니다
                  </div>
                ) : (
                  <>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-[32px] font-bold leading-none tracking-[-0.02em] text-slate-900 tabular-nums dark:text-white">
                    {workPct}
                  </span>
                  <span className="text-[14px] font-semibold text-slate-400">%</span>
                </div>

                <div className="mt-2.5 flex h-[8px] overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                  <span
                    className="h-full bg-emerald-500"
                    style={{ width: `${(workDone / workTotal) * 100}%` }}
                  />
                  <span
                    className="h-full bg-blue-500"
                    style={{ width: `${(workWorking / workTotal) * 100}%` }}
                  />
                </div>

                <div className="mt-3 border-t border-slate-100 pt-2.5 dark:border-white/5">
                  <div className="flex items-baseline text-[12px]">
                    <span className="text-slate-500 dark:text-slate-400">작업 중 위반 발생</span>
                    <span className="ml-auto tabular-nums">
                      <b
                        className={`font-semibold ${
                          jobViol > 0
                            ? "text-rose-600 dark:text-rose-400"
                            : "text-slate-900 dark:text-white"
                        }`}
                      >
                        {jobViol}
                      </b>
                      <span className="text-slate-400"> / {jobTotal}건</span>
                    </span>
                  </div>
                  {jobRate != null && (
                    <div className="mt-1.5 text-[11.5px] text-slate-400">
                      작업 단위 준수율{" "}
                      <b className="font-semibold text-slate-600 dark:text-slate-300">
                        {jobRate}%
                      </b>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 grid shrink-0 grid-cols-3 divide-x divide-slate-200 rounded-xl border border-slate-200 dark:divide-white/10 dark:border-white/10">
            <div className="px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                오늘 위반
              </div>
              <div className="mt-1.5 text-[22px] font-semibold leading-none text-slate-900 tabular-nums dark:text-white">
                {todayCount}
                <span className="ml-0.5 text-[12px] font-medium text-slate-400">건</span>
              </div>
            </div>
            <div className="px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                조치 완료
              </div>
              <div className="mt-1.5 text-[22px] font-semibold leading-none text-emerald-600 tabular-nums dark:text-emerald-400">
                {doneList.length}
                <span className="ml-0.5 text-[12px] font-medium text-slate-400">건</span>
              </div>
            </div>
            <div className="px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
                준수율 · 7일
              </div>
              <div className="mt-1.5 text-[22px] font-semibold leading-none text-slate-900 tabular-nums dark:text-white">
                {compliance == null ? "—" : compliance}
                {compliance != null && (
                  <span className="ml-0.5 text-[12px] font-medium text-slate-400">%</span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex min-h-0 flex-1 flex-col">
            <Trend daily={week?.daily} />
          </div>

          {byItem.length > 0 && (
            <div className="mt-4 shrink-0">
              <div className="mb-2 text-[12px] font-medium text-slate-500 dark:text-slate-400">
                오늘 항목별
              </div>
              <div className="flex flex-col gap-2">
                {byItem.map((r) => (
                  <div key={r.item || r.label} className="flex items-center gap-3">
                    <span className="w-[108px] shrink-0 truncate text-[12px] text-slate-600 dark:text-slate-300">
                      {r.label}
                    </span>
                    <span className="h-[6px] flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                      <i
                        className="block h-full rounded-full bg-slate-400 dark:bg-slate-500"
                        style={{
                          width: `${Math.round((Number(r.count || 0) / maxItem) * 100)}%`,
                        }}
                      />
                    </span>
                    <span className="w-[34px] shrink-0 text-right text-[12px] font-semibold tabular-nums text-slate-900 dark:text-white">
                      {r.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 오른쪽 — 오늘 조치 내역 */}
        <div className="flex w-[320px] shrink-0 flex-col border-l border-slate-200 dark:border-white/10">
          <div className="flex h-[46px] shrink-0 items-center border-b border-slate-200 px-4 dark:border-white/10">
            <span className="text-[13px] font-semibold text-slate-900 dark:text-white">
              오늘 조치 내역
            </span>
            <span className="ml-auto text-[12px] tabular-nums text-slate-400">
              {doneList.length}건
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {doneList.length === 0 ? (
              <div className="flex h-full items-center justify-center px-4 text-center text-[12.5px] text-slate-400">
                오늘 처리한 건이 없습니다
              </div>
            ) : (
              doneList.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 last:border-0 dark:border-white/5"
                >
                  <Icon.Check size={15} className="shrink-0 text-emerald-500" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-medium text-slate-800 dark:text-slate-100">
                      {v.itemLabel}
                    </span>
                    <span className="block truncate text-[11.5px] text-slate-500 dark:text-slate-400">
                      {v.siteLabel}
                    </span>
                  </span>
                  <span className="shrink-0 text-[11.5px] tabular-nums text-slate-400">
                    {fmtClock(v.timestamp)}
                  </span>
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={() => onNavigate?.("events")}
            className="flex h-[42px] shrink-0 items-center justify-center gap-1.5 border-t border-slate-200 text-[12.5px] font-medium text-slate-500 transition hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/5"
          >
            전체 이벤트
            <Icon.Right size={13} />
          </button>
        </div>
      </div>
    </Card>
  );
}
